import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import { Children, forwardRef } from "react";
import { cn } from "../../utils/index.js";
import { Divider } from "../Divider/index.js";
import { Heading } from "../Heading/index.js";
import { Image } from "../Image/index.js";
import { Li, Ol, Ul } from "../Lists/index.js";
import { PlainA } from "../PlainA/index.js";
import { Table } from "../Table/index.js";
import { TBody } from "../Table/TBody/index.js";
import { Td } from "../Table/Td/index.js";
import { Th } from "../Table/Th/index.js";
import { THead } from "../Table/THead/index.js";
import { Tr } from "../Table/Tr/index.js";
import { P } from "../Text/index.js";
import { Span } from "../Text/Span.js";
import { mergeEmbeddedEntryRegistry, resolveEmbeddedEntryContentType } from "./HorizonContentfulRichText.embeds.js";
import { horizonContentfulRichTextVariants } from "./HorizonContentfulRichText.variants.js";
const HorizonContentfulRichText_HorizonContentfulRichText = /*#__PURE__*/ forwardRef(function({ className, document, renderers, variant = 'article', emphasis = 'regular' }, ref) {
    const slots = horizonContentfulRichTextVariants({
        variant,
        emphasis
    });
    const resolveAsset = renderers?.resolveAsset ?? defaultResolveAsset;
    const resolveEntryHref = renderers?.resolveEntryHref;
    const renderInlineEntry = renderers?.renderInlineEntry;
    const renderBlockEntry = renderers?.renderBlockEntry;
    const sanitizeHref = renderers?.sanitizeHref ?? defaultSanitizeHref;
    const embeddedEntryRegistry = mergeEmbeddedEntryRegistry(renderers?.embeddedEntryRenderers);
    return /*#__PURE__*/ jsx("div", {
        ref: ref,
        className: cn(slots.root(), className),
        children: documentToReactComponents(document, createContentfulOptions({
            slots,
            emphasis,
            resolveAsset,
            resolveEntryHref,
            renderBlockEntry,
            renderInlineEntry,
            sanitizeHref,
            embeddedEntryRegistry
        }))
    });
});
function createContentfulOptions({ slots, emphasis, resolveAsset, resolveEntryHref, renderBlockEntry, renderInlineEntry, sanitizeHref, embeddedEntryRegistry }) {
    const headingShade = 'bold' === emphasis ? '1' : '13';
    const headingPalette = 'bold' === emphasis ? 'primary' : 'neutral';
    const dividerEmphasis = 'bold' === emphasis ? 'bold' : 'regular';
    const headingCursor = {
        currentH2Id: void 0
    };
    return {
        renderMark: {
            [MARKS.BOLD]: (text)=>/*#__PURE__*/ jsx(Span, {
                    bold: true,
                    children: text
                }),
            [MARKS.ITALIC]: (text)=>/*#__PURE__*/ jsx(Span, {
                    italic: true,
                    children: text
                }),
            [MARKS.UNDERLINE]: (text)=>/*#__PURE__*/ jsx(Span, {
                    underline: "solid",
                    children: text
                }),
            [MARKS.CODE]: (text)=>/*#__PURE__*/ jsx("code", {
                    className: slots.code(),
                    children: text
                })
        },
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children)=>{
                if (isEmptyTrailingNode(node)) return;
                return /*#__PURE__*/ jsx(P, {
                    className: slots.paragraph(),
                    textWrap: "pretty",
                    children: children
                });
            },
            [BLOCKS.HEADING_1]: (_node, children)=>/*#__PURE__*/ jsx(Heading, {
                    as: "h1",
                    className: slots.headingH1(),
                    palette: headingPalette,
                    shade: headingShade,
                    textWrap: "balance",
                    children: children
                }),
            [BLOCKS.HEADING_2]: (node, children)=>/*#__PURE__*/ jsx(Heading, {
                    as: "h2",
                    id: headingAnchorId('h2', node, headingCursor),
                    className: slots.headingH2(),
                    palette: headingPalette,
                    shade: headingShade,
                    textWrap: "balance",
                    children: children
                }),
            [BLOCKS.HEADING_3]: (node, children)=>/*#__PURE__*/ jsx(Heading, {
                    as: "h3",
                    id: headingAnchorId('h3', node, headingCursor),
                    className: slots.headingH3(),
                    palette: headingPalette,
                    shade: headingShade,
                    textWrap: "balance",
                    children: children
                }),
            [BLOCKS.HEADING_4]: (_node, children)=>/*#__PURE__*/ jsx(Heading, {
                    as: "h4",
                    className: slots.headingH4(),
                    palette: headingPalette,
                    shade: headingShade,
                    textWrap: "balance",
                    children: children
                }),
            [BLOCKS.HEADING_5]: (_node, children)=>/*#__PURE__*/ jsx(Heading, {
                    as: "h5",
                    className: slots.headingH5(),
                    palette: headingPalette,
                    shade: headingShade,
                    textWrap: "balance",
                    children: children
                }),
            [BLOCKS.HEADING_6]: (_node, children)=>/*#__PURE__*/ jsx(Heading, {
                    as: "h6",
                    className: slots.headingH6(),
                    palette: headingPalette,
                    shade: headingShade,
                    textWrap: "balance",
                    children: children
                }),
            [BLOCKS.QUOTE]: (_node, children)=>/*#__PURE__*/ jsx("blockquote", {
                    className: slots.blockquote(),
                    children: children
                }),
            [BLOCKS.HR]: ()=>/*#__PURE__*/ jsx(Divider, {
                    className: slots.divider(),
                    emphasis: dividerEmphasis,
                    lineSlotBeforeClassName: "m-0",
                    lineSlotAfterClassName: "m-0"
                }),
            [BLOCKS.UL_LIST]: (_node, children)=>/*#__PURE__*/ jsx(Ul, {
                    className: slots.ul(),
                    children: children
                }),
            [BLOCKS.OL_LIST]: (_node, children)=>/*#__PURE__*/ jsx(Ol, {
                    className: slots.ol(),
                    children: children
                }),
            [BLOCKS.LIST_ITEM]: (_node, children)=>/*#__PURE__*/ jsx(Li, {
                    className: slots.li(),
                    children: children
                }),
            [INLINES.HYPERLINK]: (node, children)=>{
                const safeHref = sanitizeHref(getContentfulUri(node));
                if (!safeHref) return /*#__PURE__*/ jsx(Fragment, {
                    children: children
                });
                const { rel, target } = getLinkTargetProps(safeHref);
                return /*#__PURE__*/ jsx(PlainA, {
                    href: safeHref,
                    variant: "inline",
                    type: "primary",
                    emphasis: emphasis,
                    rel: rel,
                    target: target,
                    children: children
                });
            },
            [INLINES.ENTRY_HYPERLINK]: (node, children)=>{
                const href = resolveEntryHref?.(getContentfulTarget(node));
                const safeHref = href ? sanitizeHref(href) : void 0;
                if (!safeHref) return /*#__PURE__*/ jsx(Fragment, {
                    children: children
                });
                const { rel, target } = getLinkTargetProps(safeHref);
                return /*#__PURE__*/ jsx(PlainA, {
                    href: safeHref,
                    variant: "inline",
                    type: "primary",
                    emphasis: emphasis,
                    rel: rel,
                    target: target,
                    children: children
                });
            },
            [INLINES.EMBEDDED_ENTRY]: (node)=>renderInlineEntry?.(getContentfulTarget(node)) ?? null,
            [BLOCKS.EMBEDDED_ENTRY]: (node)=>{
                const target = getContentfulTarget(node);
                const contentTypeId = resolveEmbeddedEntryContentType(target);
                const registered = contentTypeId && Object.hasOwn(embeddedEntryRegistry, contentTypeId) ? embeddedEntryRegistry[contentTypeId] : void 0;
                const content = registered ? registered(target) : renderBlockEntry?.(target);
                return content ? /*#__PURE__*/ jsx("div", {
                    className: slots.embed(),
                    children: content
                }) : null;
            },
            [BLOCKS.EMBEDDED_ASSET]: (node)=>{
                const asset = resolveAsset(getContentfulTarget(node));
                if (!asset) return null;
                return /*#__PURE__*/ jsxs("figure", {
                    className: slots.figure(),
                    children: [
                        /*#__PURE__*/ jsx(Image, {
                            src: asset.src,
                            alt: asset.alt,
                            borderRadius: "xl",
                            width: "full"
                        }),
                        asset.caption ? /*#__PURE__*/ jsx("figcaption", {
                            className: slots.figcaption(),
                            children: asset.caption
                        }) : null
                    ]
                });
            },
            [BLOCKS.TABLE]: (node, children)=>{
                const childRows = Children.toArray(children);
                const { head, body } = splitTableChildren(node, childRows);
                return /*#__PURE__*/ jsx("div", {
                    className: slots.table(),
                    children: /*#__PURE__*/ jsxs(Table, {
                        children: [
                            head ? /*#__PURE__*/ jsx(THead, {
                                children: head
                            }) : null,
                            /*#__PURE__*/ jsx(TBody, {
                                children: body
                            })
                        ]
                    })
                });
            },
            [BLOCKS.TABLE_ROW]: (_node, children)=>/*#__PURE__*/ jsx(Tr, {
                    children: children
                }),
            [BLOCKS.TABLE_HEADER_CELL]: (_node, children)=>/*#__PURE__*/ jsx(Th, {
                    children: /*#__PURE__*/ jsx("div", {
                        className: slots.tableCellContent(),
                        children: children
                    })
                }),
            [BLOCKS.TABLE_CELL]: (_node, children)=>/*#__PURE__*/ jsx(Td, {
                    children: /*#__PURE__*/ jsx("div", {
                        className: slots.tableCellContent(),
                        children: children
                    })
                })
        }
    };
}
function isRecord(value) {
    return 'object' == typeof value && null !== value;
}
function toHeadingId(node) {
    const id = trimHyphens(collectNodeText(node).normalize('NFKD').replaceAll(/[\u0300-\u036f]/g, '').toLowerCase().replaceAll(/['’]/g, '').replaceAll(/[^\p{L}\p{N}]+/gu, '-'));
    return '' === id ? void 0 : id;
}
function trimHyphens(value) {
    let start = 0;
    let end = value.length;
    while(start < end && '-' === value.charAt(start))start += 1;
    while(end > start && '-' === value.charAt(end - 1))end -= 1;
    return value.slice(start, end);
}
function headingAnchorId(as, node, cursor) {
    if ('h2' !== as && 'h3' !== as) return;
    const slug = toHeadingId(node);
    if ('h2' === as) {
        cursor.currentH2Id = slug;
        return slug;
    }
    if (!slug) return;
    return cursor.currentH2Id ? `${cursor.currentH2Id}_${slug}` : slug;
}
function collectNodeText(node) {
    if ('text' === node.nodeType) {
        const value = node.value;
        return 'string' == typeof value ? value : '';
    }
    const content = node.content;
    if (!Array.isArray(content)) return '';
    return content.map((child)=>isRecord(child) ? collectNodeText(child) : '').join('');
}
function isEmptyTrailingNode(node) {
    const content = node.content;
    if (!Array.isArray(content) || 1 !== content.length) return false;
    const only = content[0];
    return isRecord(only) && 'text' === only.nodeType && '' === only.value;
}
function getContentfulUri(node) {
    const data = node.data;
    if (!isRecord(data)) return '';
    const uri = data.uri;
    return 'string' == typeof uri ? uri : '';
}
function getContentfulTarget(node) {
    const data = node.data;
    if (!isRecord(data)) return;
    return data.target;
}
function defaultResolveAsset(target) {
    if (!isRecord(target)) return;
    const fields = isRecord(target.fields) ? target.fields : void 0;
    const fileFromFields = fields && isRecord(fields.file) ? fields.file : void 0;
    const fileFromTopLevel = isRecord(target.file) ? target.file : void 0;
    const file = fileFromFields ?? fileFromTopLevel;
    const urlCandidate = (file && (file.url ?? (isRecord(file.file) ? file.file.url : void 0))) ?? target.url ?? (fields ? fields.url : void 0);
    const rawSrc = 'string' == typeof urlCandidate ? urlCandidate : void 0;
    if (!rawSrc) return;
    const src = rawSrc.startsWith('//') ? `https:${rawSrc}` : rawSrc;
    const altCandidate = (fields ? fields.description ?? fields.title : void 0) ?? target.description ?? target.title;
    const alt = 'string' == typeof altCandidate ? altCandidate : '';
    const captionCandidate = (fields ? fields.title : void 0) ?? target.title;
    const caption = 'string' == typeof captionCandidate ? captionCandidate : void 0;
    return {
        src,
        alt,
        caption
    };
}
function defaultSanitizeHref(href) {
    if (!href) return;
    if (href.startsWith('/') || href.startsWith('#')) return href;
    if (href.startsWith('//')) return `https:${href}`;
    try {
        const parsed = new URL(href);
        if ('http:' === parsed.protocol || 'https:' === parsed.protocol || 'mailto:' === parsed.protocol || 'tel:' === parsed.protocol) return href;
        return;
    } catch  {
        return;
    }
}
function getLinkTargetProps(href) {
    if (href.startsWith('/') || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return {};
    return {
        target: '_blank',
        rel: 'noreferrer noopener'
    };
}
function splitTableChildren(node, children) {
    const content = node.content;
    const rows = Array.isArray(content) ? content : [];
    const firstRow = rows[0];
    const firstRowContent = isRecord(firstRow) ? firstRow.content : void 0;
    const firstRowCells = Array.isArray(firstRowContent) ? firstRowContent : [];
    const isHeaderRow = firstRowCells.length > 0 && firstRowCells.every((cell)=>isRecord(cell) && cell.nodeType === BLOCKS.TABLE_HEADER_CELL);
    if (!isHeaderRow) return {
        body: children
    };
    const [head, ...body] = children;
    return {
        head,
        body
    };
}
export { HorizonContentfulRichText_HorizonContentfulRichText as HorizonContentfulRichText };
